import React, { useState } from "react";
import { CheckCircle } from "lucide-react";
import { EnhancedPlan } from "../types";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import SubscriptionSuccess from "./SubscriptionSuccess";

interface PlanCardProps {
    plan: EnhancedPlan;
    isCurrent: boolean;
    isHighlighted?: boolean;
    currentSubscriptionName?: string;
    onClick?: () => void;
    accessCode?: string;
    discountAmount?: number;
}

const PlanCard: React.FC<PlanCardProps> = ({
    plan,
    isCurrent,
    isHighlighted = false,
    currentSubscriptionName = "",
    onClick,
    accessCode,
    discountAmount = 0,
}) => {
    const navigate = usePartnerAwareNavigate();
    const [showSuccess, setShowSuccess] = useState(false);

    // Determine if plan is free, plus, or pro
    const isPlanFree = plan.price === 0;
    const isPlanPlus = plan.name.toLowerCase().includes("plus");
    const isPlanPro = plan.name.toLowerCase().includes("pro");

    // Determine if this plan is the current subscription
    const isCurrentPlanByName = currentSubscriptionName
        ?.toLowerCase()
        .includes(
            isPlanFree ? "basic" : isPlanPlus ? "plus" : isPlanPro ? "pro" : ""
        );

    // Check if user has a paid subscription
    const hasPaidSubscription =
        currentSubscriptionName?.toLowerCase().includes("plus") ||
        currentSubscriptionName?.toLowerCase().includes("pro");

    // Check if user has pro subscription
    const hasProSubscription = currentSubscriptionName
        ?.toLowerCase()
        .includes("pro");

    // Determine button disabled state based on subscription rules
    const isButtonDisabled =
        isCurrentPlanByName || // Current plan is always disabled
        (isPlanFree && hasPaidSubscription) || // Free plan disabled for paid subscriptions
        (isPlanPlus && hasProSubscription) || // Plus plan disabled for pro subscriptions
        (isPlanFree && hasProSubscription); // Free plan disabled for pro subscriptions

    const handleSubscribe = () => {
        if (onClick) {
            onClick();
        }

        if (isPlanFree) {
            setShowSuccess(true);
        } else {
            // Navigate to checkout page with plan ID and access code in the URL
            const params = new URLSearchParams({ planId: plan.id });
            if (accessCode) {
                params.append('accessCode', accessCode);
                params.append('discount', discountAmount.toString());
            }
            navigate(`/checkout?${params.toString()}`);
        }
    };

    // Get theme color for success popup
    const getThemeColor = () => {
        if (isPlanPlus) return "purple";
        if (isPlanPro) return "orange";
        return "purple";
    };

    // Styling based on plan type
    const getCardStyles = () => {
        const hasDiscount = accessCode && discountAmount > 0 && !isPlanFree;
        const baseStyle = "transition-all duration-300 bg-white";

        if (isPlanFree) {
            return {
                card: `${baseStyle} border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg`,
                button: "bg-gray-800 hover:bg-gray-900 text-white",
                checkmark: "text-gray-500",
            };
        } else if (isPlanPlus) {
            return {
                card: `${baseStyle} ${
                    hasDiscount ? 'border-2 border-green-500 shadow-lg' : 
                    isHighlighted ? 'border-2 border-blue-500 shadow-xl scale-105' : 
                    'border-2 border-gray-200'
                } hover:shadow-xl`,
                button: hasDiscount ? 
                    "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg" : 
                    "bg-blue-600 hover:bg-blue-700 text-white",
                checkmark: hasDiscount ? "text-green-500" : "text-blue-500",
            };
        } else {
            return {
                card: `${baseStyle} ${
                    hasDiscount ? 'border-2 border-green-500 shadow-lg' : 
                    'border-2 border-gray-200'
                } hover:shadow-xl`,
                button: hasDiscount ? 
                    "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg" : 
                    "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white",
                checkmark: hasDiscount ? "text-green-500" : "text-orange-500",
            };
        }
    };

    const styles = getCardStyles();

    const hasDiscount = accessCode && discountAmount > 0 && !isPlanFree;

    return (
        <>
            <div
                className={`rounded-lg p-6 flex flex-col relative ${styles.card}`}>
                {hasDiscount && (
                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg z-20">
                        DISCOUNT ACTIVE
                    </div>
                )}
                {isCurrentPlanByName ? (
                    <Badge
                        className={`self-start mb-2 ${
                            isPlanFree
                                ? "bg-purple-100 text-purple-800 border-purple-200"
                                : isPlanPlus
                                ? "bg-green-100 text-green-800 border-green-200"
                                : "bg-orange-100 text-orange-800 border-orange-200"
                        }`}>
                        Current Plan
                    </Badge>
                ) : (
                    isHighlighted &&
                    !isCurrentPlanByName && (
                        <Badge
                            className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 z-10 ${
                                isPlanFree
                                    ? "bg-purple-100 text-purple-800 border-2 border-purple-300"
                                    : isPlanPlus
                                    ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                                    : "bg-orange-100 text-orange-800 border-2 border-orange-300"
                            }`}>
                            Recommended
                        </Badge>
                    )
                )}

                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    {plan.name.replace('WeKruit ', '')}
                </h3>

                <div className="mb-4">
                    {accessCode && discountAmount > 0 && !isPlanFree && (
                        <div className="bg-green-50 border border-green-300 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">-${discountAmount}</span>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <p className="text-green-800 text-sm font-semibold">
                                        Promo Applied!
                                    </p>
                                    <p className="text-green-600 text-xs">
                                        Code: {accessCode}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex items-baseline">
                        {accessCode && discountAmount > 0 && !isPlanFree ? (
                            <>
                                <span className="text-4xl font-bold text-green-600">
                                    ${Math.max(0, plan.price - discountAmount).toFixed(2)}
                                </span>
                                <span className="text-xl text-gray-400 line-through ml-3">
                                    ${plan.price.toFixed(2)}
                                </span>
                            </>
                        ) : (
                            <span className="text-4xl font-bold text-gray-900">
                                {isPlanFree ? 'Free' : `$${plan.price.toFixed(2)}`}
                            </span>
                        )}
                        {plan.interval && !isPlanFree && (
                            <span className="text-base text-gray-500 ml-2">
                                / 1 month
                            </span>
                        )}
                    </div>

                    {plan.trialPeriod ? (
                        <p
                            className={`text-sm mt-1 ${
                                isPlanFree
                                    ? "text-purple-600"
                                    : isPlanPlus
                                    ? "text-green-600"
                                    : "text-orange-600"
                            }`}>
                            {plan.trialPeriod.formatted} free trial
                        </p>
                    ) : (
                        <p
                            className={`text-sm mt-1 ${
                                isPlanFree
                                    ? "text-purple-600"
                                    : isPlanPlus
                                    ? "text-green-600"
                                    : "text-orange-600"
                            }`}>
                            Free trial
                        </p>
                    )}
                </div>

                <ul className="space-y-3 mt-2 mb-6">
                    {isPlanFree && (
                        <>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    One-time access to 5 mock interviews
                                    (lifetime)
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Limited access to our question bank
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Personalized performance report
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Access to community forum and support
                                </span>
                            </li>
                        </>
                    )}
                    {isPlanPlus && (
                        <>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Up to 50 mock interview per month
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Full access to our question banks
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Personalized analytics & improvement
                                    tracking
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Entry into our exclusive hiring partner
                                    database
                                </span>
                            </li>
                        </>
                    )}
                    {isPlanPro && (
                        <>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Includes all Plus features, and adds:
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Unlimited practice
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Priority support from dedicated MBB
                                    consultants
                                </span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                                <CheckCircle
                                    className={`h-5 w-5 ${styles.checkmark} mt-0.5 flex-shrink-0`}
                                />
                                <span className="text-sm">
                                    Live practice session with expert
                                    consultants
                                </span>
                            </li>
                        </>
                    )}
                </ul>

                <div className="mt-auto">
                    {/* Button */}
                    <Button
                        className={`w-full py-3 text-base font-semibold ${isButtonDisabled ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : styles.button} transition-all duration-200 mt-6`}
                        onClick={handleSubscribe}
                        disabled={isButtonDisabled}>
                        {isCurrentPlanByName
                            ? "Current Plan"
                            : isButtonDisabled
                            ? "Not Available"
                            : isPlanFree
                            ? "Get Started Free"
                            : hasDiscount
                            ? `Get Started - $${Math.max(0, plan.price - discountAmount)}/mo`
                            : `Get Started - $${plan.price}/mo`}
                    </Button>
                </div>
            </div>
            {showSuccess && (
                <SubscriptionSuccess
                    onClose={() => setShowSuccess(false)}
                    planName={plan.name}
                    themeColor={getThemeColor()}
                />
            )}
        </>
    );
};

export default PlanCard;
